/**
 * Formulário de produto da etapa 2. O estado do produto decide a unidade da capacidade e se há
 * ponto de fulgor; depois de cadastrado o estado não muda, para não reinterpretar a unidade (§3.2).
 */
import React from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import {
  FLAMMABLE_PHYSICAL_STATES,
  ROTULO_DO_ESTADO_FISICO,
} from '../../../../../types/flammableInstallation';
import { UNIDADE_DO_ESTADO, type ProdutoErrors, type ProdutoValues } from './produtoDoFormulario';

interface FormularioDoProdutoProps {
  titulo: string;
  values: ProdutoValues;
  errors: ProdutoErrors;
  /** Frase do servidor quando ele recusou o produto. */
  erroDoServidor: string | null;
  editando: boolean;
  salvando: boolean;
  onChange: (patch: Partial<ProdutoValues>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const CAIXA_COM_TOQUE_DE_44 = { p: 1.25 } as const;

export const FormularioDoProduto: React.FC<FormularioDoProdutoProps> = ({
  titulo,
  values,
  errors,
  erroDoServidor,
  editando,
  salvando,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const liquido = values.physicalState === 'LIQUIDO';

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        onSubmit();
      }}
      sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <Typography variant="subtitle1" component="h4" fontWeight={700} sx={{ mb: 2 }}>
        {titulo}
      </Typography>
      {erroDoServidor && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erroDoServidor}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
          gap: 2,
          '& .MuiFormHelperText-root': { fontSize: '1rem' },
        }}
      >
        <TextField
          required
          label="Nome do produto"
          placeholder="Ex.: Gás liquefeito de petróleo (GLP)"
          value={values.productName}
          onChange={(event) => onChange({ productName: event.target.value })}
          error={Boolean(errors.productName)}
          helperText={errors.productName}
          disabled={salvando}
          sx={{ gridColumn: { md: 'span 6' } }}
        />
        <TextField
          select
          required
          label="Estado"
          value={values.physicalState}
          onChange={(event) =>
            onChange({ physicalState: event.target.value as ProdutoValues['physicalState'] })
          }
          disabled={salvando || editando}
          helperText={
            editando
              ? 'O estado não muda depois de cadastrado. Para trocar, exclua o produto e cadastre de novo.'
              : 'Gás é sempre gás inflamável.'
          }
          sx={{ gridColumn: { md: 'span 3' } }}
        >
          {FLAMMABLE_PHYSICAL_STATES.map((estado) => (
            <MenuItem key={estado} value={estado} sx={{ minHeight: 44 }}>
              {ROTULO_DO_ESTADO_FISICO[estado]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          label={`Capacidade (${UNIDADE_DO_ESTADO[values.physicalState]})`}
          placeholder="Ex.: 30"
          value={values.capacity}
          onChange={(event) => onChange({ capacity: event.target.value })}
          error={Boolean(errors.capacity)}
          helperText={errors.capacity ?? 'Líquido em m³; gás em toneladas. Decimais com vírgula (10000,5), sem ponto de milhar.'}
          disabled={salvando}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ gridColumn: { md: 'span 3' } }}
        />
        {liquido && (
          <>
            <TextField
              required
              label="Ponto de fulgor (°C)"
              placeholder="Ex.: 38"
              value={values.flashPoint}
              onChange={(event) => onChange({ flashPoint: event.target.value })}
              error={Boolean(errors.flashPoint)}
              helperText={errors.flashPoint ?? 'Está na ficha de segurança do produto.'}
              disabled={salvando}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              sx={{ gridColumn: { md: 'span 3' } }}
            />
            <FormControlLabel
              sx={{ gridColumn: { md: 'span 5' }, alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={values.storedHeated}
                  onChange={(event) => onChange({ storedHeated: event.target.checked })}
                  disabled={salvando}
                  sx={CAIXA_COM_TOQUE_DE_44}
                />
              }
              label="Armazenado aquecido, a temperatura igual ou acima do ponto de fulgor"
            />
          </>
        )}
        <FormControlLabel
          sx={{ gridColumn: { md: 'span 4' }, alignItems: 'flex-start', m: 0 }}
          control={
            <Checkbox
              checked={values.sdsAvailable}
              onChange={(event) => onChange({ sdsAvailable: event.target.checked })}
              disabled={salvando}
              sx={CAIXA_COM_TOQUE_DE_44}
            />
          }
          label="Tem ficha com dados de segurança (FISPQ)"
        />
        <TextField
          label="Onde fica"
          placeholder="Ex.: Tanque 1"
          value={values.storageLocation}
          onChange={(event) => onChange({ storageLocation: event.target.value })}
          disabled={salvando}
          sx={{ gridColumn: { md: 'span 4' } }}
        />
        <TextField
          label="Observação"
          placeholder="Algo que o laudo deva registrar"
          value={values.observations}
          onChange={(event) => onChange({ observations: event.target.value })}
          disabled={salvando}
          sx={{ gridColumn: { md: 'span 8' } }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button variant="text" onClick={onCancel} disabled={salvando} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
        <Button type="submit" variant="outlined" disabled={salvando} sx={{ minHeight: 44 }}>
          Salvar produto
        </Button>
      </Box>
    </Box>
  );
};

export default FormularioDoProduto;
